import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAnalytics } from 'firebase/analytics';
import { getFunctions } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { Router } from './router.js';
import { HomePage } from './pages/home.js';
import { WhitepaperPage } from './pages/whitepaper.js';
import { createDownloadFormPage } from './pages/download-form.js';
import { closeDownloadModal } from './components/modal.js';
import { getPageMeta } from './utils/seo-meta.js';
import './components/mobile-menu.js';
import { initScrollToTop } from './components/scroll-to-top.js';
// import './components/cookie-banner.js'; // 一時的に無効化（広告ブロッカー対策）

// Firebaseの設定（環境変数から取得）
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};


// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
// const analytics = getAnalytics(app); // 一時的にコメントアウト
const analytics = null; // Firebase Analyticsを無効化
const functions = getFunctions(app);
const auth = getAuth(app);

// ルーター初期化
const router = new Router();
// グローバルにアクセス可能にする
window.router = router;

// ページ遷移時のメタデータ更新
function updatePageMeta(pathname) {
  const meta = getPageMeta(pathname);
  document.title = meta.title;

  // canonical URLの更新
  let canonicalLink = document.querySelector('link[rel="canonical"]');
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    document.head.appendChild(canonicalLink);
  }
  const canonicalUrl = `https://purchase-master.jp${pathname}`;
  canonicalLink.href = canonicalUrl;

  // descriptionの更新
  let descriptionMeta = document.querySelector('meta[name="description"]');
  if (descriptionMeta) {
    descriptionMeta.content = meta.description;
  }

  // keywordsの更新
  let keywordsMeta = document.querySelector('meta[name="keywords"]');
  if (!keywordsMeta) {
    keywordsMeta = document.createElement('meta');
    keywordsMeta.name = 'keywords';
    document.head.appendChild(keywordsMeta);
  }
  keywordsMeta.content = meta.keywords;

  // OGPの更新
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) ogTitle.content = meta.title;

  const ogDescription = document.querySelector('meta[property="og:description"]');
  if (ogDescription) ogDescription.content = meta.description;

  const ogUrl = document.querySelector('meta[property="og:url"]');
  if (ogUrl) ogUrl.content = canonicalUrl;

  // Twitter Cardの更新
  const twitterTitle = document.querySelector('meta[name="twitter:title"]');
  if (twitterTitle) twitterTitle.content = meta.title;

  const twitterDescription = document.querySelector('meta[name="twitter:description"]');
  if (twitterDescription) twitterDescription.content = meta.description;
}

// ルート定義
router.on('/', () => {
  updatePageMeta('/');
  document.getElementById('app').innerHTML = HomePage();
});

// 購入仲介マスター トップページ
router.on('/master', () => {
  updatePageMeta('/master');
  import('./pages/master-top.js').then(module => {
    document.getElementById('app').innerHTML = module.MasterTopPage();
  });
});

// 購入仲介ラボ
router.on('/master/lab', () => {
  updatePageMeta('/master/lab');
  import('./pages/lab.js').then(module => {
    document.getElementById('app').innerHTML = module.LabPage();
  });
});

// ラボカテゴリーページ
router.on('/master/lab/:category', (params) => {
  updatePageMeta(`/master/lab/${params.category}`);
  import('./pages/lab-category.js').then(module => {
    document.getElementById('app').innerHTML = module.LabCategoryPage(params);
  });
});

// ラボ記事ページ
router.on('/master/lab/:category/:articleId', (params) => {
  updatePageMeta(`/master/lab/${params.category}/${params.articleId}`);
  import('./pages/lab-article-simple.js').then(module => {
    document.getElementById('app').innerHTML = module.LabArticlePage(params);
  });
});

// 購入仲介とは？ページ
router.on('/master/about', () => {
  updatePageMeta('/master/about');
  import('./pages/about-purchase.js').then(module => {
    document.getElementById('app').innerHTML = module.AboutPurchasePage();
  });
});

// 購入仲介用語集
router.on('/master/glossary', () => {
  updatePageMeta('/master/glossary');
  import('./pages/glossary.js').then(module => {
    document.getElementById('app').innerHTML = module.GlossaryPage();
  });
});

// 問い合わせフォーム - Google Formsにリダイレクト
router.on('/master/contact', () => {
  // Google FormsのURLにリダイレクト
  window.location.href = 'https://docs.google.com/forms/d/e/1FAIpQLSfxt8-DAq1Aj0MacSbBSWeBW2BxoIxsk2QkDIkxWDBuLvlY3Q/viewform';
});


// 資料ダウンロード一覧
router.on('/master/download', () => {
  updatePageMeta('/master/download');
  import('./pages/download-list.js').then(module => {
    document.getElementById('app').innerHTML = module.DownloadListPage();
  });
});

// 個別ダウンロードページ
router.on('/master/download/:whitepaperId', (params) => {
  updatePageMeta(`/master/download/${params.whitepaperId}`);
  import('./pages/nansho-download-form.js').then(module => {
    document.getElementById('app').innerHTML = module.DownloadFormPage(params);
  });
});

// 旧URLからのリダイレクト（互換性保持）
router.on('/download/:whitepaperId', (params) => {
  window.location.href = `/master/download/${params.whitepaperId}`;
});

router.on('/lab', () => {
  window.location.href = '/master/lab';
});

// 404ページ
router.on('/404', () => {
  document.title = 'ページが見つかりません | 反響成約マスター';
  import('./pages/404.js').then(module => {
    document.getElementById('app').innerHTML = module.render404();
  });
});

// プライバシーポリシー
router.on('/privacy', () => {
  document.title = 'プライバシーポリシー | 反響成約マスター';
  import('./pages/privacy.js').then(module => {
    document.getElementById('app').innerHTML = module.PrivacyPage();
  });
});

// 利用規約
router.on('/terms', () => {
  document.title = '利用規約 | 反響成約マスター';
  import('./pages/terms.js').then(module => {
    document.getElementById('app').innerHTML = module.TermsPage();
  });
});

// クッキーポリシー
router.on('/cookie-policy', () => {
  document.title = 'クッキーポリシー | 反響成約マスター';
  import('./pages/policy-cookies.js').then(module => {
    document.getElementById('app').innerHTML = module.CookiePolicyPage();
    module.initCookiePolicy();
  });
});

// 代替URL（Brave対策）
router.on('/site-policy', () => {
  document.title = 'サイトポリシー | 反響成約マスター';
  import('./pages/policy-cookies.js').then(module => {
    document.getElementById('app').innerHTML = module.CookiePolicyPage();
    module.initCookiePolicy();
  });
});

// ページの初期化
document.addEventListener('DOMContentLoaded', () => {
  setupModalHandlers();
  router.init();
  
  // スクロールトップボタンを初期化
  initScrollToTop();
  
  // パフォーマンス最適化
  import('./utils/lazy-load.js').then(module => {
    module.initLazyLoad();
  });
  
  // DNS プリフェッチ
  import('./utils/performance.js').then(module => {
    module.dnsPrefetch([
      'fonts.googleapis.com',
      'fonts.gstatic.com',
      'www.googletagmanager.com'
    ]);
  });
});

// モーダル関連のイベントハンドラー設定
function setupModalHandlers() {
  // モーダルを閉じる
  document.addEventListener('click', (e) => {
    if (e.target.classList.contains('close') || e.target.classList.contains('modal')) {
      closeDownloadModal();
    }
  });
}

export { db, analytics, functions, auth };